import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Camera, ScanLine, X, Sparkles, Lock, ShieldCheck } from 'lucide-react';

export default function ScanPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isVenueSide = user?.role === 'venue_owner' || user?.role === 'admin';
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState('');
  const scannerRef = useRef(null);
  const containerId = 'th-qr-reader';
  const lastSentRef = useRef(0);

  useEffect(() => {
    if (!isVenueSide) return;
    api.get('/venues', { params: { limit: 500 } }).then((r) => {
      setVenues(r.data);
      if (user?.venue_id) setVenueId(user.venue_id);
      else if (r.data[0]) setVenueId(r.data[0].id);
    });
  }, [isVenueSide, user]);

  const submitScan = async (qr_payload) => {
    if (!venueId) {
      toast.error('Pick a venue first');
      return;
    }
    // throttle: 1 scan per 1.5 seconds
    if (Date.now() - lastSentRef.current < 1500) return;
    lastSentRef.current = Date.now();
    try {
      const r = await api.post('/checkins/scan', { qr_payload, venue_id: venueId });
      setResult(r.data);
      if (r.data.duplicate) toast.info('Already checked in recently');
      else toast.success('Check-in recorded');
      stopScanner();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    }
  };

  const startScanner = async () => {
    if (!venueId) { toast.error('Pick a venue first'); return; }
    setScanning(true);
    setResult(null);
    try {
      const html5 = new Html5Qrcode(containerId);
      scannerRef.current = html5;
      await html5.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => submitScan(decoded),
        () => {}
      );
    } catch (e) {
      setScanning(false);
      toast.error('Camera permission denied');
    }
  };

  const stopScanner = async () => {
    try {
      await scannerRef.current?.stop();
      await scannerRef.current?.clear();
    } catch {}
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => { return () => { stopScanner(); }; }, []);

  const submitManual = () => {
    if (!manual.trim()) return;
    submitScan(manual.trim());
  };

  if (!isVenueSide) {
    return (
      <div className="min-h-screen">
        <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
          <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
            <Logo size={96} withText={false} />
          </div>
        </header>
        <main className="mx-auto max-w-2xl space-y-4 px-4 py-6">
          <GlassCard className="p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F6B24A]/15 text-[#F6B24A]">
                <Lock className="h-5 w-5" />
              </div>
              <div>
                <h1 className="font-display text-xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Venue Scanner</h1>
                <p className="mt-1 text-sm text-white/70">
                  The QR scanner is for venue owners and admins. To redeem your discount,
                  show <span className="text-[#F6B24A]">your personal QR</span> at the bar.
                </p>
                <Button onClick={() => nav('/profile/qr')} className="mt-4 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="scan-show-my-qr-button">
                  <Sparkles className="mr-2 h-4 w-4" /> Show my QR
                </Button>
              </div>
            </div>
          </GlassCard>
          <GlassCard className="p-4 text-sm text-white/70">
            Venue owner? <button onClick={() => nav('/auth/login')} className="text-[#F6B24A] hover:underline" data-testid="scan-venue-owner-login-link">Log in as venue owner</button> to access the scanner.
          </GlassCard>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <Logo size={96} withText={false} />
          <div className="flex items-center gap-2 rounded-full border border-[#2DE2E6]/30 bg-[#2DE2E6]/10 px-3 py-1">
            <ShieldCheck className="h-4 w-4 text-[#2DE2E6]" />
            <span className="text-xs font-semibold text-[#2DE2E6]">Venue mode</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-4">
        <GlassCard className="p-4">
          <h1 className="font-display text-2xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>QR Scanner</h1>
          <p className="mt-1 text-sm text-white/70">Scan a customer's Thirrsty QR to record check-in and apply discount.</p>
          <div className="mt-3">
            <label className="text-xs uppercase tracking-wider text-white/60">Venue</label>
            <Select value={venueId} onValueChange={setVenueId}>
              <SelectTrigger className="mt-1 h-11 bg-white/5 border-white/12 text-white" data-testid="scan-venue-select">
                <SelectValue placeholder="Select a venue" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B0C10] border-white/10 text-white">
                {venues.map((v) => (
                  <SelectItem key={v.id} value={v.id} data-testid={`scan-venue-option-${v.id}`}>{v.name} — {v.city}, {v.state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-white/10 bg-black">
            <div id={containerId} className="absolute inset-0" data-testid="qr-scanner-view" />
            {!scanning && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/70">
                <Camera className="h-10 w-10" />
                <p className="text-sm">Camera will open when you start scanning</p>
              </div>
            )}
            {/* Viewfinder corners */}
            {scanning && (
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-6 top-6 h-8 w-8 border-l-2 border-t-2 border-[#F6B24A]" />
                <div className="absolute right-6 top-6 h-8 w-8 border-r-2 border-t-2 border-[#F6B24A]" />
                <div className="absolute left-6 bottom-6 h-8 w-8 border-l-2 border-b-2 border-[#F6B24A]" />
                <div className="absolute right-6 bottom-6 h-8 w-8 border-r-2 border-b-2 border-[#F6B24A]" />
              </div>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            {!scanning ? (
              <Button onClick={startScanner} className="flex-1 h-11 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="qr-scanner-open-camera-button">
                <ScanLine className="mr-2 h-4 w-4" /> Start Scanning
              </Button>
            ) : (
              <Button onClick={stopScanner} variant="outline" className="flex-1 h-11 rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10" data-testid="qr-scanner-stop-button">
                <X className="mr-2 h-4 w-4" /> Stop
              </Button>
            )}
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <p className="text-xs uppercase tracking-wider text-white/60">Manual entry (testing)</p>
          <p className="mt-1 text-xs text-white/50">Paste a Thirrsty QR payload here to simulate a scan.</p>
          <div className="mt-2 flex gap-2">
            <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="thirrsty://user/..." className="bg-white/5 border-white/12 text-white font-mono text-xs" data-testid="scan-manual-input" />
            <Button onClick={submitManual} className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid="scan-manual-submit-button">Submit</Button>
          </div>
        </GlassCard>
      </main>

      <Dialog open={!!result} onOpenChange={(v) => !v && setResult(null)}>
        <DialogContent className="max-w-md bg-[#0B0C10] border-white/10 text-white" data-testid="qr-scan-result-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{result?.duplicate ? 'Duplicate Scan' : 'Check-in Recorded'}</DialogTitle>
          </DialogHeader>
          {result && (
            <div className="space-y-3">
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs uppercase tracking-wider text-white/60">Customer</p>
                <p className="font-semibold text-white" data-testid="qr-scan-result-user">{result.user?.display_name}</p>
                <p className="text-xs text-white/60 font-mono">{result.user?.email}</p>
              </div>
              <div className="rounded-xl border border-[#F6B24A]/30 bg-[#F6B24A]/10 p-3">
                <p className="text-xs uppercase tracking-wider text-[#F6B24A]">Discount to apply</p>
                <p className="font-semibold text-white" data-testid="qr-scan-result-discount">{result.discount_text || 'No active discount'}</p>
              </div>
              <p className="text-xs text-white/60">{result.message}</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setResult(null); startScanner(); }} className="w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid="qr-redeem-discount-button">
              Done — Scan Next
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
